import { useState, useEffect, useCallback } from "react";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, AlertTriangle, History, DollarSign } from "lucide-react";
import {
  LineChart,
  Line,
  YAxis,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from "recharts";
import {
  DECISION_COLORS,
  DECISION_LABELS,
  MODE_LABELS,
  formatDateShort,
} from "@/lib/analysis-constants";
import { analysesService } from "@/services";
import type { AnalysisHistoryEntry } from "@/types";

interface RunSidebarProps {
  depotId: string;
  currentAnalysisId: string;
  onSelectAnalysis?: (analysisId: string) => void;
  depotExtractionCost?: number | null;
}

const formatCost = (value: number) => `CHF ${value.toFixed(4)}`;
const getQriBadgeClass = (level?: string) => {
  switch (level) {
    case "high":
      return "bg-success text-success-foreground";
    case "medium":
      return "bg-warning text-warning-foreground";
    case "low":
      return "bg-destructive text-destructive-foreground";
    default:
      return "bg-muted text-muted-foreground";
  }
};

export const RunSidebar = ({
  depotId,
  currentAnalysisId,
  onSelectAnalysis,
  depotExtractionCost,
}: RunSidebarProps) => {
  const [history, setHistory] = useState<AnalysisHistoryEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadHistory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await analysesService.getHistory(depotId);
      setHistory(data);
    } catch (err) {
      console.error("Erreur chargement historique:", err);
      setError("Impossible de charger l'historique");
    } finally {
      setLoading(false);
    }
  }, [depotId]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <AlertTriangle className="h-6 w-6 text-destructive mb-2" />
        <p className="text-xs text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4 text-center">
        <History className="h-6 w-6 text-muted-foreground mb-2" />
        <p className="text-xs text-muted-foreground">Aucun run</p>
      </div>
    );
  }

  const showChart = history.length > 1;

  const extractionCost = depotExtractionCost ?? 0;
  const totalDossierCost =
    extractionCost +
    history.reduce((sum, e) => sum + (e.costEstimate ?? 0), 0);

  const chartData = history.map((entry, idx) => ({
    name: `#${idx + 1}`,
    score: entry.overallScore ?? 0,
    decision: entry.decision,
    date: formatDateShort(entry.createdAt),
    mode: entry.mode ? MODE_LABELS[entry.mode] || entry.mode : "-",
    qriScore: entry.qualityReviewIndex?.score ?? null,
    qriLevel: entry.qualityReviewIndex?.level ?? null,
  }));
  const distinctConfigs = new Set(
    history.map((entry) => `${entry.mode || "legacy"}|${entry.hardness || "balanced"}|${entry.promptPolicyVersion || "legacy"}`)
  );
  const hasMixedRunConfigs = distinctConfigs.size > 1;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-3 py-2 border-b flex-shrink-0">
        <h3 className="text-sm font-semibold flex items-center gap-1.5">
          <History className="h-3.5 w-3.5" />
          Runs ({history.length})
        </h3>
      </div>

      {/* Mini chart */}
      {showChart && (
        <div className="px-2 pt-2 pb-1 border-b flex-shrink-0">
          <ResponsiveContainer width="100%" height={100}>
            <LineChart data={chartData}>
              <YAxis domain={[0, 100]} hide />
              <RechartsTooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const data = payload[0].payload;
                  return (
                    <div className="bg-popover border rounded p-2 shadow-md text-xs">
                      <p className="font-medium">{data.name} - {data.date}</p>
                      <p>Score: <span className="font-bold">{data.score}/100</span></p>
                      <p>Mode: {data.mode}</p>
                      {data.qriScore !== null && (
                        <p>QRI: <span className="font-bold">{data.qriScore}/100 ({data.qriLevel})</span></p>
                      )}
                    </div>
                  );
                }}
              />
              <Line
                type="monotone"
                dataKey="score"
                stroke="#6366f1"
                strokeWidth={2}
                dot={({ cx, cy, payload }: any) => (
                  <circle
                    key={`dot-${payload.name}`}
                    cx={cx}
                    cy={cy}
                    r={4}
                    fill={DECISION_COLORS[payload.decision] || "#888"}
                    stroke="white"
                    strokeWidth={1.5}
                  />
                )}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {hasMixedRunConfigs && (
        <div className="px-2 py-1.5 border-b bg-warning/10 text-[10px] text-warning-foreground">
          <div className="flex items-center gap-1">
            <AlertTriangle className="h-3 w-3" />
            Runs avec configurations différentes (mode/hardness/policy), comparaison prudente.
          </div>
        </div>
      )}

      {/* Run list */}
      <ScrollArea className="flex-1">
        <div className="p-1.5 space-y-0.5">
          {history.map((entry, idx) => {
            const isCurrent = entry.id === currentAnalysisId;
            const runNumber = idx + 1;
            const analysisCost = entry.costEstimate ?? 0;
            const isFirstRun = idx === 0;
            const runCost =
              isFirstRun && extractionCost > 0
                ? extractionCost + analysisCost
                : analysisCost;

            return (
              <button
                key={entry.id}
                onClick={() => onSelectAnalysis?.(entry.id)}
                className={`w-full text-left rounded-md px-2.5 py-2 transition-colors text-xs ${
                  isCurrent
                    ? "bg-primary/10 border border-primary/30"
                    : "hover:bg-muted/50 border border-transparent"
                }`}
              >
                <div className="flex items-center justify-between gap-1">
                  <span className="font-mono text-muted-foreground">
                    #{runNumber}
                  </span>
                  <span className="text-muted-foreground truncate">
                    {formatDateShort(entry.createdAt)}
                  </span>
                </div>
                <div className="flex items-center justify-between gap-1 mt-1">
                  <span className="font-mono font-semibold text-sm">
                    {entry.overallScore ?? "-"}/100
                  </span>
                  <div className="flex items-center gap-1">
                    {entry.qualityReviewIndex && (
                      <Badge className={`text-[10px] px-1.5 py-0 ${getQriBadgeClass(entry.qualityReviewIndex.level)}`}>
                        QRI {entry.qualityReviewIndex.score}
                      </Badge>
                    )}
                    {entry.decision && (
                      <Badge
                        className="text-[10px] px-1.5 py-0"
                        style={{
                          backgroundColor:
                            DECISION_COLORS[entry.decision] || "#888",
                          color: "white",
                        }}
                      >
                        {DECISION_LABELS[entry.decision] || entry.decision}
                      </Badge>
                    )}
                    {isCurrent && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0"
                      >
                        Courant
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="mt-1 text-[10px] text-muted-foreground">
                  {entry.mode ? MODE_LABELS[entry.mode] || entry.mode : "Legacy"}
                  {entry.hardness ? ` • ${entry.hardness}` : ""}
                </div>
                {runCost > 0 && (
                  <div className="mt-1 text-[10px] text-muted-foreground flex items-center gap-1">
                    <DollarSign className="h-3 w-3" />
                    {formatCost(runCost)}
                    {isFirstRun && extractionCost > 0 && (
                      <span className="text-orange-500">
                        (extr. + analyse)
                      </span>
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </ScrollArea>

      {/* Total dossier */}
      {totalDossierCost > 0 && (
        <div className="px-3 py-2 border-t flex-shrink-0 bg-muted/30">
          <div className="flex items-center justify-between text-xs">
            <span className="text-muted-foreground flex items-center gap-1">
              <DollarSign className="h-3 w-3" />
              Total dossier
            </span>
            <span className="font-semibold">{formatCost(totalDossierCost)}</span>
          </div>
        </div>
      )}
    </div>
  );
};
